import { SalesforceProduct } from "@shared/services";
import * as T from "@shared/types";
import { Service } from "@shared/types";
import { SuccessResponse } from "@shared/types/apiResponse";
import { AxiosRequestConfig } from "axios";
import { axios } from ".";
import { DEFAULT_VENDOR_LOCALE } from "../../providers/vendor/actions";
import { throwError } from "./utilities/throwing";

interface InstallShopifyServiceParams {
  shop: string;
  accessToken?: string;
}

type InstallServiceParams = InstallShopifyServiceParams;

export const SERVICE_BASE_URL = "/api/v1/services/bc-services";

export const getServices = async (serviceType?: Service["serviceType"]) => {
  const config = { params: { serviceType } };
  const response = await axios.get<{ data: Service[] }>(SERVICE_BASE_URL, config);
  return response.data.data;
};

export const installShopifyService = async (shop: string, accessToken?: string, locale?: string) => {
  const params = { shop, accessToken, locale };
  return await installService("shopify", params);
};

export const installWooCommerceService = async (shop: string, locale?: string) => {
  const params = { shop, locale };
  return await installService("woocommerce", params);
};

const installService = async (serviceId: T.ServiceId, params: InstallServiceParams) => {
  const url = `${SERVICE_BASE_URL}/${serviceId}/install`;
  try {
    const response = await axios.post(url, params);
    return response.data.data;
  } catch (e) {
    throwError("Install service error", e);
  }
};

export const getSkuImportDrafts = async (installationId: string) => {
  const url = `${SERVICE_BASE_URL}/sku-import-draft/${installationId}`;
  const response = await axios.get<T.SkuImportDraftEntry[]>(url);
  return response.data;
};

export const postSkuImportDrafts = async (vendor: T.Vendor, installationId: string, products: T.FormProduct[]) => {
  const url = `${SERVICE_BASE_URL}/sku-import-draft/${installationId}`;
  if (!vendor.contactId) return;
  try {
    return await axios.post<SalesforceProduct[], SuccessResponse>(
      url,
      setToDraftObject(sanitizeSkuImportDrafts(products), vendor.contactId, vendor.firstName + " " + vendor.lastName)
    );
  } catch (e) {
    throwError("Error in posting sku import drafts", e);
  }
};

export const patchSkuImportDrafts = async (
  vendor: T.Vendor,
  installationId: string,
  product: T.FormProduct,
  status: T.SkuImportDraftState
) => {
  const config: AxiosRequestConfig = { params: { state: status, id: product.id } };
  const url = `${SERVICE_BASE_URL}/sku-import-draft/${installationId}`;
  if (!vendor.contactId) return;
  const skuImportDraftEntry = {
    ...setToDraftObject([product], vendor.contactId, vendor.firstName + " " + vendor.lastName)[0],
    state: status,
  };
  try {
    return await axios.patch<SalesforceProduct[], SuccessResponse>(url, skuImportDraftEntry, config);
  } catch (e) {
    throwError("Error in patching sku import drafts", e);
  }
};

// prepare the object to be sent to the server
const setToDraftObject = (products: T.FormProduct[], vendorContactId: string, vendorUserName: string) => {
  return products.map((product) => {
    return {
      draftFormProduct: product,
      id: product.id,
      type: "skuImportDraft",
      vendorContactId,
      vendorUserName,
    };
  });
};

export const sanitizeSkuImportDrafts = (drafts: T.FormProduct[]) => {
  return drafts.map((draft) => {
    return {
      ...draft,
      isProductFormValid: false,
      imageUploads: undefined,
      variants: sanitizeSkuImportDraftVariants(draft.variants),
    };
  });
};

// sanitize the draft variants so that some values do not clash on the way back
export const sanitizeSkuImportDraftVariants = (draftVariants: T.FormVariant[]) => {
  return draftVariants.map((variant) => {
    return {
      ...variant,
      status: undefined,
    };
  });
};

export const getActivities = async (subjectId?: string, lastId?: string, lastCreatedAt?: string, limit?: number) => {
  const url = `${SERVICE_BASE_URL}/activities`;
  const config = { params: { subjectId, lastId, lastCreatedAt, limit } };
  const response = await axios.get<{ data: T.ActivityResult }>(url, config);
  return response.data.data;
};

export const getUserLocale = async (accessToken: string) => {
  const url = `${SERVICE_BASE_URL}/user-locale`;
  const config: AxiosRequestConfig = { params: { token: accessToken } };
  const response = await axios.get<{ data: T.Locale }>(url, config);
  return response.data.data ?? DEFAULT_VENDOR_LOCALE;
};

export const getCustomerAccountCarrierDetails = async () => {
  const url = `${SERVICE_BASE_URL}/customer-carrier-account`;
  const response = await axios.get<{ data: T.CarrierCustomerAccountBinding[] }>(url);
  return response.data.data;
};

export const setVendorProperties = async (properties: T.VendorProperties) => {
  const url = `${SERVICE_BASE_URL}/vendor-preferences`;
  try {
    const response = await axios.post<T.VendorProperties>(url, properties);
    return response.data;
  } catch (e) {
    throwError("Error in setting vendor preferences", e);
  }
};
